import { type ReactElement, useState } from 'react';
import { Badge, CheckRow, Field, Grid, Notice, Select, Stack, Text, TextInput } from '@vello/web-theme';
import { FINAL_FARE_EXAMPLE_START, finalFareExampleCategories, finalFareExampleView, type FinalFareExampleField, type FinalFareExampleForm } from '../../src/index';
import { DetailRow } from './detail-row';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * D855 (owner Q3, 2026-10-30): "Calcular um exemplo" under the `fare.finalRevision` form. The operator types a quoted trip, the
 * trip as driven and a simple tariff; the console runs the same engine a ride runs on the rule being edited (the default rule
 * or a category's) and shows the final fare, why it moved, which trigger fired and which limit held it. The amounts are example
 * values (no currency): nothing is charged or saved.
 */
export function FinalFareExample({ value }: { readonly value: unknown }): ReactElement {
  const { t, lang } = useT();
  const { reason } = useVocab();
  const [form, setForm] = useState<FinalFareExampleForm>(FINAL_FARE_EXAMPLE_START);
  const categories = finalFareExampleCategories(value);
  const view = finalFareExampleView(value, form.category !== '' && !categories.includes(form.category) ? { ...form, category: '' } : form);
  const amount = (minor: bigint): string => new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(minor) / 100);
  const set = (field: keyof FinalFareExampleForm, next: string | boolean): void => setForm((f) => ({ ...f, [field]: next }));

  const input = (field: FinalFareExampleField, unit: 'amount' | 'km' | 'minutes'): ReactElement => (
    <Field label={t(`govset.ffx.${field}`)} htmlFor={`govset-ffx-${field}`} hint={t(`govset.ffx.unit.${unit}`)}>
      <TextInput id={`govset-ffx-${field}`} inputMode="decimal" value={form[field]} onChange={(e) => set(field, e.target.value)} />
    </Field>
  );

  return (
    <Stack gap={3}>
      <Text role="title" as="h3" block>{t('govset.ffx.title')}</Text>
      <Text role="caption" tone="secondary" block>{t('govset.ffx.sub')}</Text>
      {categories.length > 0 && (
        <Field label={t('govset.ffx.category')} htmlFor="govset-ffx-category">
          <Select id="govset-ffx-category" value={form.category} onChange={(e) => set('category', e.target.value)}>
            <option value="">{t('govset.ffx.category.default')}</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
      )}
      <Grid min="xs" gap={3}>
        {input('quoted', 'amount')}
        {input('quotedKm', 'km')}
        {input('quotedMinutes', 'minutes')}
        {input('drivenKm', 'km')}
        {input('drivenMinutes', 'minutes')}
        {input('perKm', 'amount')}
        {input('perMinute', 'amount')}
      </Grid>
      <CheckRow id="govset-ffx-evidence" checked={form.distanceEvidenced} onChange={(c) => set('distanceEvidenced', c)}>
        {t('govset.ffx.evidenced')}
      </CheckRow>
      <div aria-live="polite">
        {view.kind === 'incomplete' && <Notice tone="neutral">{t('govset.ffx.incomplete')}</Notice>}
        {view.kind === 'bad_input' && <Notice tone="warning">{t('govset.ffx.badInput', { field: t(`govset.ffx.${view.field}`) })}</Notice>}
        {view.kind === 'ok' && (
          <Stack gap={2}>
            <DetailRow mono label={t('govset.ffx.final')} value={`${amount(view.finalMinor)} (${view.differenceMinor > 0n ? '+' : ''}${amount(view.differenceMinor)})`} />
            <div>
              <Badge tone={view.direction === 'UP' ? 'warning' : 'neutral'}>{t(`govset.ffx.direction.${view.direction}`)}</Badge>
            </div>
            <DetailRow mono={false} label={t('govset.ffx.reason')} value={reason(view.reasonCode)} />
            <DetailRow mono={false} label={t('govset.ffx.trigger')} value={reason(view.triggerCode)} />
            {view.capCode !== undefined && <DetailRow mono={false} label={t('govset.ffx.cap')} value={reason(view.capCode)} />}
          </Stack>
        )}
      </div>
    </Stack>
  );
}
